import { useMemo } from "react";
import { evaluateVisibility } from "../../shared/visibility/evaluator";
import { extractDataSources } from "../../shared/visibility/extractDataSources";
import { useVisibilityContext } from "./useVisibilityContext";
import { useOpenLoopChatIntegration } from "../../shared/store/portalSchemaStore";
import type { DataSourceType } from "../../shared/schema/visibility";

export interface UseOpenLoopChatVisibilityResult {
  /** Whether the widget should be visible given its configured visibility condition */
  isVisible: boolean;
  /** Whether visibility data is still loading */
  isLoading: boolean;
}

/**
 * Hook to check if the OpenLoop chat widget should be visible based on its optional
 * visibility condition (phased-rollout targeting). Mirrors useBlockVisibility's
 * data-fetching/evaluation pattern. No condition configured => always visible.
 */
export function useOpenLoopChatVisibility(): UseOpenLoopChatVisibilityResult {
  const condition = useOpenLoopChatIntegration()?.visibility?.condition;

  const requiredSources = useMemo(() => {
    if (!condition) return new Set<DataSourceType>();
    return extractDataSources(condition);
  }, [condition]);

  const { context, isLoading } = useVisibilityContext({ requiredSources });

  const isVisible = useMemo(() => {
    // No visibility condition = always visible
    if (!condition) return true;

    // While loading, hide the widget to avoid injecting then immediately tearing it down
    if (!context) return false;

    return evaluateVisibility(condition, context);
  }, [condition, context]);

  return { isVisible, isLoading };
}
